// WI-1280933 [Security] AI Pentest remediation — Finding #12996 (Portal XSS).
// Centralized HTML sanitization for every surface that renders server- or
// user-influenced markup via dangerouslySetInnerHTML or react-markdown raw HTML.
//
// Design goals:
//  - Fail CLOSED: any sanitizer error returns an empty string, never raw markup.
//  - Observability: potential XSS payloads (markup stripped by the sanitizer) are
//    reported to Application Insights so we can detect exploitation attempts.
//  - Reusable: a single DOMPurify configuration + a rehype-sanitize schema shared
//    across all rendering surfaces so behavior stays consistent.

import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

// Lazy Application Insights singleton — mirrors the pattern used elsewhere in the
// portal (see MarkdownMessage.tsx / errorHandling) to avoid duplicate SDK init.
let _appInsights: ApplicationInsights | null = null;
function getAppInsights(): ApplicationInsights | null {
  try {
    if (!_appInsights) {
      _appInsights = new ApplicationInsights({
        config: { instrumentationKey: process.env.REACT_APP_INSTRUMENTATION_KEY },
      });
      _appInsights.loadAppInsights();
    }
    return _appInsights;
  } catch {
    // Telemetry must never break rendering; degrade silently.
    return null;
  }
}

// Custom URL schemes the portal relies on for in-app markdown actions. These must
// survive sanitization (see MarkdownMessage.tsx customUrlTransform).
const ALLOWED_ACTION_PROTOCOLS = [
  'create-ticket-action',
  'escalate-action',
  'ticket-card',
  'send-prompt-action',
];

// Force safe external-link semantics on any anchor that opens a new tab.
// Registered once; DOMPurify hooks are global to the module instance.
let _hooksInstalled = false;
function ensureHooks(): void {
  if (_hooksInstalled) {
    return;
  }
  try {
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node instanceof Element && node.tagName === 'A' && node.getAttribute('target') === '_blank') {
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
    _hooksInstalled = true;
  } catch {
    // If hook registration is unavailable, sanitization still proceeds safely.
  }
}

// Shared DOMPurify options. DOMPurify's defaults already strip <script>, event
// handlers and javascript:/data: URIs; we additionally allow the target attribute
// (hardened via the hook above) and permit our custom action schemes.
const PURIFY_CONFIG: Config = {
  ADD_ATTR: ['target'],
  ALLOW_UNKNOWN_PROTOCOLS: false,
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel|ftp|create-ticket-action|escalate-action|ticket-card|send-prompt-action):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
};

// Conservative signature of markup that indicates an XSS attempt. Detection is
// driven off the RAW input (not DOMPurify's opaque internal bookkeeping, whose
// `removed` array is unreliable across builds) so the telemetry signal is
// deterministic: if any of these appear, the sanitizer necessarily neutralized
// them. Kept intentionally narrow to avoid false positives on benign markup.
const DANGEROUS_MARKUP =
  /<\s*(?:script|iframe|object|embed|svg|math|link|meta|base)\b|\son\w+\s*=|javascript\s*:|vbscript\s*:|data\s*:\s*text\/html/i;

/**
 * Sanitize an untrusted HTML fragment for safe use with dangerouslySetInnerHTML.
 * Fails closed (returns '') on any error and reports stripped markup to telemetry.
 *
 * @param dirty  Raw HTML string (may be null/undefined).
 * @param context Short identifier of the render surface, used for telemetry.
 * @returns A sanitized HTML string that is safe to inject into the DOM.
 */
export function sanitizeHtml(dirty: string | null | undefined, context: string): string {
  if (dirty === null || dirty === undefined) {
    return '';
  }

  const input = typeof dirty === 'string' ? dirty : String(dirty);
  if (input.length === 0) {
    return '';
  }

  try {
    ensureHooks();
    const clean = DOMPurify.sanitize(input, PURIFY_CONFIG) as string;

    // Detect XSS attempts from the raw input. A match means the sanitizer just
    // stripped/neutralized dangerous markup — a strong exploitation signal on
    // user- or server-influenced content.
    if (DANGEROUS_MARKUP.test(input)) {
      getAppInsights()?.trackEvent({
        name: 'HTML_SANITIZER_BLOCKED_MARKUP',
        properties: {
          context,
          inputLength: String(input.length),
        },
      });
    }

    return clean;
  } catch (error) {
    // Fail closed: never return the raw, unsanitized markup.
    getAppInsights()?.trackException({
      exception: error instanceof Error ? error : new Error('sanitizeHtml failed'),
      properties: { context },
    });
    return '';
  }
}

/**
 * Dependency-free HAST sanitizer used as a rehype plugin for react-markdown
 * surfaces that enable raw HTML via rehype-raw. Replaces `rehype-sanitize`
 * (which — along with its `hast-util-sanitize` engine — cannot be installed in
 * every environment). Runs AFTER rehypeRaw so any raw HTML has already been
 * parsed into element nodes, then:
 *   - drops dangerous elements outright (script/style/iframe/object/...),
 *   - unwraps unknown elements (keeps their text children),
 *   - strips event-handler attributes, inline styles and disallowed attributes,
 *   - blocks javascript:/vbscript:/data: URLs on href/src (safe + portal action
 *     schemes are allow-listed),
 *   - forces rel="noopener noreferrer" on target="_blank" anchors.
 */

// Safe, formatting-only element allow-list (mirrors the GitHub/rehype-sanitize
// default schema plus GFM task-list <input>). Everything else is unwrapped.
const ALLOWED_TAGS = new Set<string>([
  'a', 'abbr', 'b', 'blockquote', 'br', 'caption', 'code', 'col', 'colgroup',
  'dd', 'del', 'details', 'div', 'dl', 'dt', 'em', 'figcaption', 'figure',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'input', 'ins', 'kbd',
  'li', 'mark', 'ol', 'p', 'pre', 'q', 'rp', 'rt', 'ruby', 's', 'samp',
  'section', 'small', 'span', 'strike', 'strong', 'sub', 'summary', 'sup',
  'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul', 'var', 'wbr',
]);

// Elements whose content is itself dangerous — removed entirely (with children).
const DROP_TAGS = new Set<string>([
  'script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'base',
  'form', 'input[type=hidden]', 'svg', 'math', 'noscript', 'template',
  'frame', 'frameset', 'applet', 'audio', 'video', 'source', 'track',
]);

// Attributes permitted on any element.
const GLOBAL_ATTR = new Set<string>(['classname', 'class', 'id', 'title', 'align', 'dir', 'lang']);

// Additional attributes permitted per tag (lower-cased attribute names).
const TAG_ATTR: { [tag: string]: Set<string> } = {
  a: new Set(['href', 'name', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height', 'loading']),
  input: new Set(['type', 'checked', 'disabled']),
  ol: new Set(['start', 'reversed', 'type']),
  li: new Set(['value']),
  td: new Set(['colspan', 'rowspan', 'headers']),
  th: new Set(['colspan', 'rowspan', 'scope', 'headers']),
  col: new Set(['span', 'width']),
  colgroup: new Set(['span']),
  details: new Set(['open']),
};

// URLs that are safe on href/src: standard web schemes, fragments/relative paths,
// and the portal's custom action schemes.
const SAFE_URL_REGEXP = new RegExp(
  '^(?:(?:https?|mailto|tel|ftp|' +
    ALLOWED_ACTION_PROTOCOLS.join('|') +
    '):|#|/|\\./|\\.\\./|[^:]*(?:$|[?#/]))',
  'i'
);

function isSafeUrl(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  // Strip control chars/whitespace that are used to smuggle "java\tscript:" etc.
  const normalized = value.replace(/[\u0000-\u0020]+/g, '').toLowerCase();
  if (/^(?:javascript|vbscript|data):/i.test(normalized)) {
    return false;
  }
  return SAFE_URL_REGEXP.test(value.trim());
}

interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: { [key: string]: unknown };
  children?: HastNode[];
}

function sanitizeNode(node: HastNode): HastNode[] {
  if (!node || typeof node !== 'object') {
    return [];
  }

  if (node.type === 'comment') {
    return [];
  }

  if (node.type !== 'element') {
    // text / other leaf nodes pass through untouched.
    return [node];
  }

  const tag = String(node.tagName || '').toLowerCase();

  if (DROP_TAGS.has(tag)) {
    return [];
  }

  // Recurse first so unwrapped children are already clean.
  node.children = sanitizeChildren(node.children);

  if (!ALLOWED_TAGS.has(tag)) {
    // Unknown but not explicitly dangerous — unwrap, keeping safe children.
    return node.children || [];
  }

  const props = (node.properties || {}) as { [key: string]: unknown };
  const clean: { [key: string]: unknown } = {};
  const perTag = TAG_ATTR[tag];

  for (const key of Object.keys(props)) {
    const lk = key.toLowerCase();
    if (lk.startsWith('on')) {
      continue; // event handlers (onclick, onerror, ...)
    }
    if (lk === 'style') {
      continue; // inline CSS can carry expression()/url() vectors
    }
    const allowed = GLOBAL_ATTR.has(lk) || (perTag ? perTag.has(lk) : false);
    if (!allowed) {
      continue;
    }
    if ((tag === 'a' && lk === 'href') || (tag === 'img' && lk === 'src')) {
      if (!isSafeUrl(props[key])) {
        continue; // drop unsafe URL entirely
      }
    }
    clean[key] = props[key];
  }

  // Harden external links opened in a new tab.
  if (tag === 'a' && clean.target === '_blank') {
    clean.rel = 'noopener noreferrer';
  }

  node.properties = clean;
  return [node];
}

function sanitizeChildren(children: HastNode[] | undefined): HastNode[] {
  const out: HastNode[] = [];
  for (const child of children || []) {
    for (const sanitized of sanitizeNode(child)) {
      out.push(sanitized);
    }
  }
  return out;
}

/**
 * rehype plugin factory. Usage: `rehypePlugins={[rehypeRaw, rehypeSanitizePlugin]}`.
 * Must run AFTER rehypeRaw.
 */
export function rehypeSanitizePlugin() {
  return (tree: HastNode): HastNode => {
    try {
      tree.children = sanitizeChildren(tree.children);
    } catch (error) {
      // Fail closed: on any error, drop all content rather than risk XSS.
      getAppInsights()?.trackException({
        exception: error instanceof Error ? error : new Error('rehypeSanitizePlugin failed'),
        properties: { context: 'markdown-hast' },
      });
      tree.children = [];
    }
    return tree;
  };
}

// Generated by Copilot
// updated on: 2026-07-21 | new file | [Auto Generated] | US 1280933 AC-3 (#12996): shared fail-closed DOMPurify sanitizer + dependency-free HAST rehype sanitizer with App Insights logging | Time Taken 25 min
